import { createContext, useContext, type ReactNode } from 'react';
import { usePhoneLayout } from './PhoneLayout';

interface CardProps {
  children: ReactNode;
  className?: string;
  padding?: boolean;
}

interface CardHeaderProps {
  title: string;
  action?: ReactNode;
}

/** True inside a Card — lets a nested Card know it is the second level. */
const CardNestingContext = createContext(false);

/**
 * Story #22197 (BR-MOB-008) — one card level on a phone: with the phone layout on, a Card rendered
 * inside another Card drops its own frame (border, shadow, background, side padding) below 768px, so
 * a phone never shows a box inside a box. 768px and up, and the client portal (phone layout off), are
 * unchanged.
 */
const NESTED_PHONE_CLASSES = 'max-md:border-0 max-md:bg-transparent max-md:shadow-none max-md:px-0';

export function Card({ children, className = '', padding = true }: CardProps) {
  const phone = usePhoneLayout();
  const nested = useContext(CardNestingContext);
  const flatten = phone && nested;
  return (
    <CardNestingContext.Provider value={true}>
      <div
        className={`bg-white rounded-lg shadow-sm border border-gray-200 ${padding ? 'p-5' : ''} ${className}${flatten ? ` ${NESTED_PHONE_CLASSES}` : ''}`}
        data-card-nested={flatten ? 'true' : undefined}
      >
        {children}
      </div>
    </CardNestingContext.Provider>
  );
}

Card.Header = function CardHeader({ title, action }: CardHeaderProps) {
  return (
    <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-200">
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      {action}
    </div>
  );
};
